/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Typed REST + WebSocket client for the Zentrani API.
 *
 * Resolves the API base from VITE_API_URL (build) or falls back to same-origin /api.
 * Credentials are always sent (httpOnly session cookie). CSRF token is read from the
 * zs_csrf cookie and echoed in the X-CSRF-Token header on unsafe methods (double-submit).
 *
 * NOTE: a few `any` types are intentional for opaque API payloads (audit rows, env vars).
 */

export type Role =
  | "resident"
  | "guard"
  | "admin"
  | "frequent_visitor"
  | "platform_admin"
  | "billing_contact";

export interface SessionInfo {
  user_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: Role;
  community_id: string | null;
  community_name: string | null;
  csrf_token: string;
}

/** Native app session (`POST /api/auth/app/*`). `token` is sent as `Authorization: Bearer`. */
export interface AppSession {
  token: string;
  token_type: "bearer";
  expires_at: string;
  user_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: Role;
  community_id: string;
  community_slug: string;
  community_name: string;
}

export interface AppCommunity {
  slug: string;
  name: string;
}

/** One community → `session`; several → `ticket` + `communities` for `appSession()`. */
export type AppLoginResult =
  | { status: "session"; session: AppSession; ticket: null; communities: null }
  | { status: "choose_community"; session: null; ticket: string; communities: AppCommunity[] };

/** A device logged in to the current account (Ajustes → Dispositivos). */
export interface SessionDevice {
  id: string;
  client: "web" | "app";
  label: string;
  community_name: string | null;
  created_at: string;
  last_seen_at: string | null;
  current: boolean;
}

export interface WsTicket {
  ticket: string;
  expires_in: number;
}

export type AuthType = "one_time" | "temporary" | "frequent";

export interface Authorization {
  id: string;
  resident_user_id: string | null;
  visitor_user_id: string | null;
  visitor_name: string;
  phone: string | null;
  visitor_email: string | null;
  document_id?: string | null;
  category_id: string | null;
  auth_type: AuthType;
  status: "active" | "consumed" | "expired" | "revoked";
  expires_at: string;
  notes: string | null;
  temp_link_token: string | null;
  event_id?: string | null;
  event_title?: string | null;
  visitor_invite_pending?: boolean | null;
  invite_activation_url?: string | null;
}

export interface VerificationResult {
  level: "authorized" | "review" | "denied";
  title: string;
  reason: string | null;
  authorization_id: string | null;
  visitor_name: string | null;
  resident_ref: string | null;
  valid_until: string | null;
  walk_in_request_id: string | null;
  issued_by_community?: boolean;
}

export interface VisitorAccessInfo {
  status: "active" | "consumed" | "expired" | "revoked";
  authorization_id: string;
  expires_at: string;
  totp_secret: string;
  totp_period_seconds: number;
  qr_token?: string | null;
  qr_refresh_seconds?: number;
}

export interface VisitorTotpEnroll {
  authorization_id: string;
  totp_secret: string;
  totp_period_seconds: number;
  expires_at: string;
  status: "active" | "consumed" | "expired" | "revoked";
}

export interface WalkInRequest {
  id: string;
  guard_user_id: string;
  resident_user_id: string;
  visitor_name: string;
  notes: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled";
  decided_at: string | null;
  created_at: string;
  authorization_id?: string | null;
}

export interface GuardResident {
  user_id: string;
  full_name: string;
  unit_label: string | null;
  open_to_visitors: boolean;
  visitor_status?: "receiving" | "confirm" | "paused";
}

export interface BatchGuestIn {
  visitor_name: string;
  phone: string;
  document_id?: string | null;
}

export interface VisitorEvent {
  id: string;
  resident_user_id: string;
  title: string;
  auth_type: "one_time" | "temporary";
  category_id: string | null;
  notes: string | null;
  starts_at: string;
  expires_at: string;
  event_date?: string | null;
  guest_count: number;
  join_token: string | null;
  join_password: string | null;
}

export interface EventJoinInfo {
  title: string;
  host_first_name: string;
  starts_at: string;
  expires_at: string;
  remaining_slots: number;
}

export interface BatchOccasionalResult {
  event: VisitorEvent;
  created: Authorization[];
  skipped: { index?: number; visitor_name?: string; reason: string; email?: string }[];
  emailed: number;
}

export interface GuardVisitorEvent {
  id: string;
  title: string;
  resident_user_id: string;
  resident_name: string;
  unit_label: string | null;
  auth_type: "one_time" | "temporary";
  expires_at: string;
  guest_count: number;
  pending_count: number;
}

export interface GuardEventGuest {
  authorization_id: string;
  visitor_name: string;
  document_id: string | null;
  phone_last3: string | null;
  status: Authorization["status"];
  entered: boolean;
}

export interface GuardVisitorEventDetail extends GuardVisitorEvent {
  guests: GuardEventGuest[];
}

export interface AccessEvent {
  id: string;
  authorization_id: string | null;
  walk_in_request_id: string | null;
  visitor_name: string;
  status: string;
  verification_method: string | null;
  guard_user_id: string | null;
  gate: string | null;
  entry_at: string | null;
  exit_at: string | null;
  created_at: string;
}

export interface Category {
  id: string;
  name: string;
  is_system: boolean;
}

export interface Metrics {
  daily_visitors: number;
  monthly_visitors: number;
  active_visitors: number;
  approvals: number;
  denials: number;
}

export interface AuditLogRow {
  id: string;
  action: string;
  role?: string | null;
  visitor_ref?: string | null;
  resident_ref?: string | null;
  gate?: string | null;
  ip?: string | null;
  created_at: string;
}

export interface AuditPage {
  items: AuditLogRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface CommunityMember {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  role: Role;
  unit_label: string | null;
  invite_pending?: boolean;
}

export interface AdminGuestAccess {
  id: string;
  visitor_name: string;
  visitor_email: string | null;
  phone: string | null;
  visitor_user_id: string | null;
  visitor_invite_pending: boolean;
  category_id: string | null;
  category_name: string | null;
  auth_type: AuthType;
  event_id: string | null;
  event_title: string | null;
  expires_at: string;
  resident_user_id: string | null;
  resident_name: string | null;
  resident_email: string | null;
  unit_label: string | null;
  status: string;
  created_at: string;
  temp_link_token?: string | null;
}

export interface AdminVisitorEvent {
  id: string;
  title: string;
  resident_user_id: string;
  resident_name: string;
  resident_email: string | null;
  unit_label: string | null;
  starts_at: string;
  expires_at: string;
  guest_count: number;
}

export interface AdminGuestAccessDirectory {
  authorizations: AdminGuestAccess[];
  events: AdminVisitorEvent[];
}

export interface CommunityMemberCreate {
  full_name: string;
  email: string;
  password: string;
  role: "resident" | "guard";
  unit_label?: string | null;
}

export interface UserImportRowError {
  row: number;
  field: string | null;
  message: string;
}

export interface UserImportResult {
  created: number;
  emails_sent: number;
  emails_failed: { email: string; detail: string }[];
  /** Set when invitations are queued for background delivery (bulk import / bulk resend). */
  emails_queued?: number;
}

export interface InviteInfo {
  full_name: string;
  email: string | null;
  community_name: string;
  role: string;
}

export interface ForgotPasswordResult {
  ok: boolean;
  message: string;
}

export interface ResetPasswordResult {
  ok: boolean;
  message: string;
}

export interface ProfileInfo {
  user_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  unit_label: string | null;
}

export interface MembershipCommunity {
  community_id: string;
  name: string;
  role: Role;
  /** Community subdomain (the native app uses it to pick the API host). */
  slug?: string | null;
}

export interface NotificationSettings {
  push_enabled: boolean;
}

export interface VisitorAvailability {
  status: "receiving" | "confirm" | "paused";
  open_to_visitors: boolean;
  unavailable_until: string | null;
  confirm_required: boolean;
}

export interface InAppNotification {
  id: string;
  kind: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface CommunityAdmin {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  settings: Record<string, unknown>;
  member_count: number;
  admin_count: number;
  active_authorizations: number;
  created_at: string;
}

export interface CommunityAdminMember {
  id: string;
  full_name: string;
  email: string | null;
  is_active: boolean;
  role: "admin";
  invite_pending: boolean;
}

export interface CommunityAdminInviteResult {
  member: CommunityAdminMember;
  user_created: boolean;
  invite_email_sent: boolean;
  invite_email_error?: string | null;
  sent_to?: string | null;
}

export interface CommunitySmtp {
  enabled: boolean;
  host: string;
  port: number;
  username: string;
  from_email: string;
  from_name: string;
  use_tls: boolean;
  use_ssl: boolean;
  has_password: boolean;
}

export interface CommunitySmtpInput {
  enabled: boolean;
  host: string;
  port: number;
  username: string;
  /** Omit or empty to keep the existing password. */
  password?: string | null;
  from_email: string;
  from_name: string;
  use_tls: boolean;
  use_ssl: boolean;
}

export interface PlatformBillingSettings {
  smtp: CommunitySmtp;
  portal_url: string | null;
  portal_url_effective: string;
}

export interface PlatformBillingSettingsInput {
  smtp: CommunitySmtpInput;
  portal_url?: string | null;
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  community: string | null;
  message: string;
  read_at: string | null;
  created_at: string;
}

export type SaasInvoiceStatus = "open" | "partially_paid" | "paid" | "overdue";
export type SaasPaymentStatus = "submitted" | "approved" | "rejected";

export interface SaasBillingContact {
  id: string;
  user_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
}

export interface SaasCommunitySubscription {
  id: string;
  slug: string;
  name: string;
  monthly_amount: string;
  paid_through: string | null;
  is_active: boolean;
}

export interface SaasBillingAccount {
  id: string;
  name: string;
  currency: BillingCurrency;
  reference_prefix: string;
  is_active: boolean;
  billing_day: number;
  contacts: SaasBillingContact[];
  communities: SaasCommunitySubscription[];
  created_at: string;
}

export interface SaasContactAddResult {
  account: SaasBillingAccount;
  user_created: boolean;
  invite_email_sent: boolean;
  invite_email_error?: string | null;
  sent_to?: string | null;
  /** One-time reveal for platform admin when a temp password was just set. */
  temporary_password?: string | null;
}

export interface SaasPlatformBankAccount {
  id: string;
  label: string;
  bank_name: string;
  account_holder: string;
  account_number: string;
  account_type: string;
  currency: BillingCurrency;
  instructions: string | null;
  is_active: boolean;
  display_order: number;
}

export interface SaasPlatformBankAccountInput {
  label: string;
  bank_name: string;
  account_holder: string;
  account_number: string;
  account_type?: "checking" | "savings";
  currency?: BillingCurrency;
  instructions?: string | null;
  is_active?: boolean;
  display_order?: number;
}

export interface SaasInvoiceLine {
  id: string;
  community_id: string;
  community_name: string | null;
  community_slug: string | null;
  description: string;
  amount: string;
  amount_paid: string;
  balance: string;
}

export interface SaasInvoice {
  id: string;
  billing_account_id: string;
  billing_account_name: string | null;
  period_year: number;
  period_month: number;
  reference_code: string;
  currency: BillingCurrency;
  status: SaasInvoiceStatus;
  subtotal: string;
  total: string;
  amount_paid: string;
  balance: string;
  issued_at: string;
  due_at: string;
  notes: string | null;
  lines: SaasInvoiceLine[];
}

export interface SaasPaymentAllocation {
  id: string;
  invoice_line_id: string;
  amount: string;
  is_applied: boolean;
  community_id: string | null;
  community_name: string | null;
}

export interface SaasPaymentProof {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface SaasPayment {
  id: string;
  billing_account_id: string;
  invoice_id: string;
  submitted_by_user_id: string;
  method: PaymentMethod | string;
  status: SaasPaymentStatus;
  amount: string;
  currency: BillingCurrency;
  platform_bank_account_id: string | null;
  paid_at: string | null;
  reference_code: string | null;
  notes: string | null;
  admin_notes: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  allocations: SaasPaymentAllocation[];
  proofs: SaasPaymentProof[];
  invoice_reference: string | null;
  billing_account_name: string | null;
}

export interface SaasPaymentCreateInput {
  invoice_id: string;
  method: PaymentMethod;
  platform_bank_account_id?: string | null;
  paid_at?: string | null;
  reference_code?: string | null;
  notes?: string | null;
  allocations: { invoice_line_id: string; amount: string }[];
}

export interface SaasBillingSummary {
  billing_account: SaasBillingAccount;
  open_invoice: SaasInvoice | null;
  bank_accounts: SaasPlatformBankAccount[];
  can_submit_payment: boolean;
  status_label: string;
  current_community_id: string | null;
  current_community_amount: string | null;
  current_community_paid_through: string | null;
}

export type AssemblyStatus = "draft" | "open" | "closed" | "archived";

export interface AssemblyOption {
  id: string;
  label: string;
  position: number;
}

export interface AssemblyAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface AssemblyTurnout {
  units_voted: number;
  units_total: number;
  participation_rate: number;
}

export interface OptionTally {
  option_id: string;
  label: string;
  votes: number;
}

export interface UnitParticipation {
  unit_id: string;
  unit_label: string;
  option_id: string | null;
  option_label: string | null;
  voted: boolean;
}

export interface AssemblyVote {
  option_id: string;
  unit_id: string | null;
  cast_at: string | null;
}

export interface Assembly {
  id: string;
  title: string;
  description: string | null;
  status: AssemblyStatus;
  closes_at: string;
  allow_vote_change: boolean;
  created_by_user_id: string;
  published_at: string | null;
  closed_at: string | null;
  results_published_at: string | null;
  created_at: string;
  options: AssemblyOption[];
  attachments: AssemblyAttachment[];
  my_unit_vote: AssemblyVote | null;
  tallies: OptionTally[] | null;
  turnout: AssemblyTurnout | null;
  participation: UnitParticipation[] | null;
}

export interface AssemblyListItem {
  id: string;
  title: string;
  description: string | null;
  status: AssemblyStatus;
  closes_at: string;
  allow_vote_change: boolean;
  published_at: string | null;
  closed_at: string | null;
  results_published_at: string | null;
  created_at: string;
  option_count: number;
  attachment_count: number;
  my_unit_has_voted: boolean | null;
  turnout: AssemblyTurnout | null;
}

export interface AssemblyCreateInput {
  title: string;
  description?: string | null;
  closes_at: string;
  allow_vote_change?: boolean;
  options: { label: string }[];
}

export type CommunicationStatus = "draft" | "scheduled" | "published";
export type CommunicationPriority = "informational" | "urgent" | "maintenance";
export type CommunicationDeliveryStatus = "pending" | "sent" | "failed";

export interface CommunicationAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface Communication {
  id: string;
  title: string;
  body: string;
  priority: CommunicationPriority;
  status: CommunicationStatus;
  publish_at: string | null;
  published_at: string | null;
  created_by_user_id: string;
  created_at: string;
  attachments: CommunicationAttachment[];
  read_at: string | null;
  attachment_count: number;
}

export interface CommunicationListItem {
  id: string;
  title: string;
  body: string;
  priority: CommunicationPriority;
  status: CommunicationStatus;
  publish_at: string | null;
  published_at: string | null;
  created_at: string;
  attachment_count: number;
  read_at: string | null;
}

export interface CommunicationCreateInput {
  title: string;
  body: string;
  priority?: CommunicationPriority;
  publish_at?: string | null;
}

export interface CommunicationDeliveryRow {
  user_id: string;
  full_name: string;
  email: string | null;
  push_status: CommunicationDeliveryStatus | null;
  email_status: CommunicationDeliveryStatus | null;
  push_sent_at: string | null;
  email_sent_at: string | null;
  read_at: string | null;
}

export interface CommunicationDeliverySummary {
  residents_total: number;
  push_sent: number;
  push_failed: number;
  email_sent: number;
  email_failed: number;
  read_count: number;
  rows: CommunicationDeliveryRow[];
}

export type LedgerEntryType = "income" | "expense";
export type LedgerListFilter = LedgerEntryType | "receivable";
export type LedgerItemSource = "manual" | "charge";

export type LedgerCategory =
  | "hoa_fees"
  | "special_assessment"
  | "amenities"
  | "donations"
  | "other"
  | "maintenance"
  | "utilities"
  | "security"
  | "cleaning"
  | "salaries"
  | "supplies"
  | "insurance"
  | "legal";

export interface LedgerAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface LedgerEntry {
  id: string;
  entry_type: LedgerEntryType;
  occurred_on: string;
  amount: string;
  currency: BillingCurrency | string;
  category: LedgerCategory | string;
  title: string;
  notes: string | null;
  counterparty: string | null;
  created_by_user_id: string;
  created_at: string;
  attachments: LedgerAttachment[];
  attachment_count: number;
}

export interface LedgerEntryListItem {
  id: string;
  entry_type: LedgerEntryType;
  occurred_on: string;
  amount: string;
  currency: BillingCurrency | string;
  category: LedgerCategory | string;
  title: string;
  counterparty: string | null;
  attachment_count: number;
  source: LedgerItemSource;
  charge_status: ChargeStatus | null;
  collected_amount: string;
  outstanding_amount: string;
}

export interface LedgerCurrencyTotals {
  income: string;
  expense: string;
  balance: string;
  receivable: string;
}

export interface LedgerList {
  items: LedgerEntryListItem[];
  totals: Record<string, LedgerCurrencyTotals>;
  year: number;
  month: number;
}

export interface LedgerEntryCreateInput {
  entry_type: LedgerEntryType;
  occurred_on: string;
  amount: string;
  currency?: BillingCurrency;
  category: LedgerCategory;
  title: string;
  notes?: string | null;
  counterparty?: string | null;
}

export type LedgerEntryUpdateInput = Partial<LedgerEntryCreateInput>;

export type ApprovalMode = "automatic" | "fifo";
export type ReservationStatus =
  | "requested"
  | "queued"
  | "confirmed"
  | "rejected"
  | "cancelled"
  | "completed";

export interface CommonArea {
  id: string;
  name: string;
  description: string | null;
  rules: string | null;
  capacity: number;
  approval_mode: ApprovalMode;
  open_time: string;
  close_time: string;
  days_of_week: number[];
  min_duration_minutes: number;
  max_duration_minutes: number;
  hourly_rate: string | null;
  currency: BillingCurrency | string;
  is_active: boolean;
  created_at: string;
}

export interface CommonAreaCreateInput {
  name: string;
  description?: string | null;
  rules?: string | null;
  capacity?: number;
  approval_mode?: ApprovalMode;
  open_time: string;
  close_time: string;
  days_of_week?: number[];
  min_duration_minutes?: number;
  max_duration_minutes?: number;
  hourly_rate?: string | number | null;
  currency?: BillingCurrency | string;
}

export interface Reservation {
  id: string;
  common_area_id: string;
  common_area_name: string | null;
  unit_id: string;
  unit_label: string | null;
  requested_by_user_id: string;
  starts_at: string;
  ends_at: string;
  status: ReservationStatus;
  notes: string | null;
  rejection_reason: string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  created_at: string;
  is_fifo_head: boolean | null;
  charge_id?: string | null;
  charge_amount?: string | null;
  suggested_amount?: string | null;
  currency?: BillingCurrency | string | null;
}

export interface ReservationCreateInput {
  common_area_id: string;
  starts_at: string;
  ends_at: string;
  notes?: string | null;
}

export interface CalendarEvent {
  id: string;
  common_area_id: string;
  unit_id: string;
  unit_label: string;
  status: ReservationStatus;
  starts_at: string;
  ends_at: string;
}

export type ChargeType =
  | "hoa_fee"
  | "maintenance"
  | "special_assessment"
  | "fine"
  | "amenity_reservation"
  | "late_fee"
  | "credit"
  | "other";
export type ChargeStatus = "pending" | "partially_paid" | "paid" | "overdue" | "cancelled";
export type PaymentStatus = "draft" | "submitted" | "under_review" | "approved" | "rejected" | "reversed";
export type PaymentMethod = "bank_transfer" | "bank_deposit";
export type BillingCurrency = "NIO" | "USD";

export interface BillingCharge {
  id: string;
  unit_id: string;
  charge_type: ChargeType | string;
  description: string;
  amount: string;
  amount_paid: string;
  balance: string;
  currency: string;
  status: ChargeStatus;
  issued_at: string;
  due_at: string;
  grace_until: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  parent_charge_id: string | null;
  schedule_id: string | null;
  source_type: string | null;
  source_id: string | null;
  issue_batch_id: string | null;
  created_at: string;
}

export interface BillingAllocation {
  id: string;
  charge_id: string;
  amount: string;
  is_applied: boolean;
}

export interface BillingProof {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

export interface BillingReceipt {
  id: string;
  receipt_number: string;
  payment_id: string;
  unit_id: string;
  issued_at: string;
  amount: string;
  currency: string;
  snapshot: Record<string, unknown>;
  voided_at: string | null;
  unit_label?: string | null;
}

export interface BillingPayment {
  id: string;
  unit_id: string;
  submitted_by_user_id: string;
  method: PaymentMethod | string;
  provider: string;
  status: PaymentStatus;
  amount: string;
  currency: string;
  community_bank_account_id: string | null;
  paid_at: string | null;
  reference_code: string | null;
  notes: string | null;
  admin_notes: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  reversed_at: string | null;
  reversal_reason: string | null;
  allocations: BillingAllocation[];
  proofs: BillingProof[];
  receipt: BillingReceipt | null;
  created_at: string;
}

export interface BillingBankAccount {
  id: string;
  label: string;
  bank_name: string;
  account_holder: string;
  account_number: string;
  account_type: string;
  currency: string;
  instructions: string | null;
  is_active: boolean;
  display_order: number;
}

export interface BillingStatement {
  unit_id: string;
  outstanding_by_currency: Record<string, string>;
  charges: BillingCharge[];
  payments: BillingPayment[];
}

export interface BillingDashboard {
  outstanding_by_currency: Record<string, string>;
  collected_mtd_by_currency: Record<string, string>;
  overdue_units: number;
  review_queue: number;
}

export interface BillingSchedule {
  id: string;
  name: string;
  charge_type: string;
  amount: string;
  currency: string;
  cadence: string;
  day_of_month: number;
  due_day_offset: number;
  grace_days: number;
  late_fee_type: string;
  late_fee_amount: string | null;
  late_fee_percent: string | null;
  applies_to: string;
  unit_ids: string[];
  is_active: boolean;
  next_run_at: string;
  last_run_at: string | null;
}

export interface BillingAgingUnit {
  unit_id: string;
  unit_label: string;
  bucket: "0_30" | "31_60" | "61_90" | "90_plus" | string;
  balance: string;
  charges: number;
}

export interface BillingAging {
  currency: BillingCurrency | string;
  buckets: Record<string, string>;
  units: BillingAgingUnit[];
}

export interface BillingCollectionsUnit {
  unit_id: string;
  unit_label: string;
  total: string;
  count: number;
}

export interface BillingCollections {
  currency: BillingCurrency | string;
  total: string;
  count: number;
  by_month: Record<string, string>;
  by_unit: BillingCollectionsUnit[];
}

export interface BillingUnit {
  id: string;
  label: string;
}

export interface PaymentCreateInput {
  amount: string;
  currency?: BillingCurrency;
  method: PaymentMethod;
  community_bank_account_id: string;
  paid_at?: string;
  reference_code?: string;
  notes?: string;
  allocations: { charge_id: string; amount: string }[];
  submit?: boolean;
}

export interface ChargeCreateInput {
  unit_id: string;
  charge_type: ChargeType;
  description: string;
  amount: string;
  currency?: BillingCurrency;
  due_at: string;
  grace_days?: number;
  source_type?: string;
  source_id?: string;
  metadata?: Record<string, unknown>;
}

export interface ChargeBulkCreateInput {
  unit_ids?: string[];
  all_units?: boolean;
  charge_type: ChargeType;
  description: string;
  amount: string;
  currency?: BillingCurrency;
  due_at: string;
  grace_days?: number;
}

export type ChargeCancelSkipReason = "has_payments" | "already_cancelled" | "not_found";

export interface ChargeCancelBulkInput {
  charge_ids?: string[];
  issue_batch_id?: string;
}

export interface ChargeCancelBulkResult {
  cancelled_count: number;
  skipped_count: number;
  cancelled_ids: string[];
  skipped: { id: string; reason: ChargeCancelSkipReason }[];
}

export interface ScheduleCreateInput {
  name: string;
  charge_type: ChargeType;
  amount: string;
  currency?: BillingCurrency;
  day_of_month?: number;
  due_day_offset?: number;
  grace_days?: number;
  late_fee_type?: "none" | "fixed" | "percent";
  late_fee_amount?: string;
  late_fee_percent?: string;
  applies_to?: "all_units" | "unit_list";
  unit_ids?: string[];
  is_active?: boolean;
  next_run_at?: string;
}

export interface BankAccountInput {
  label: string;
  bank_name: string;
  account_holder: string;
  account_number: string;
  account_type?: "checking" | "savings";
  currency?: BillingCurrency;
  instructions?: string;
  is_active?: boolean;
  display_order?: number;
}

// --- Runtime configuration -------------------------------------------------------------
// Web (default): same-origin / VITE_API_URL, httpOnly cookie + X-CSRF-Token (double-submit).
// Native app: configureApiClient({ baseUrl, bearerToken }) → absolute API host, Authorization
// header, no cookies and no CSRF header; the Guard socket authenticates with a WS ticket.

const DEFAULT_API_BASE: string = (import.meta as any).env?.VITE_API_URL ?? "";

export interface ApiClientConfig {
  /** REST base URL ("" = same origin). Defaults to VITE_API_URL. */
  baseUrl: string;
  /** WebSocket base (ws:// or wss://). Null = derived from a runtime baseUrl, else VITE_WS_URL / page host. */
  wsBaseUrl: string | null;
  /** Session token sent as `Authorization: Bearer`. Null = cookie + CSRF mode (web). */
  bearerToken: string | null;
}

const clientConfig: {
  runtimeBaseUrl: string | null;
  wsBaseUrl: string | null;
  bearerToken: string | null;
} = { runtimeBaseUrl: null, wsBaseUrl: null, bearerToken: null };

function trimSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

/** Override parts of the client config at runtime. `null` restores that part's default. */
export function configureApiClient(patch: Partial<ApiClientConfig>): void {
  if (patch.baseUrl !== undefined) {
    clientConfig.runtimeBaseUrl = patch.baseUrl === null ? null : trimSlash(patch.baseUrl);
  }
  if (patch.wsBaseUrl !== undefined) {
    clientConfig.wsBaseUrl = patch.wsBaseUrl === null ? null : trimSlash(patch.wsBaseUrl);
  }
  if (patch.bearerToken !== undefined) clientConfig.bearerToken = patch.bearerToken || null;
}

/** Set or clear (null) the Bearer token; null returns to cookie + CSRF mode. */
export function setBearerToken(token: string | null): void {
  configureApiClient({ bearerToken: token });
}

/** Restore the build-time defaults (same-origin web behavior). */
export function resetApiClient(): void {
  clientConfig.runtimeBaseUrl = null;
  clientConfig.wsBaseUrl = null;
  clientConfig.bearerToken = null;
}

export function getApiClientConfig(): ApiClientConfig {
  return {
    baseUrl: clientConfig.runtimeBaseUrl ?? DEFAULT_API_BASE,
    wsBaseUrl: clientConfig.wsBaseUrl,
    bearerToken: clientConfig.bearerToken,
  };
}

/** URL for an API path using the current base ("" base = same-origin path). */
export function apiUrl(path: string): string {
  return `${clientConfig.runtimeBaseUrl ?? DEFAULT_API_BASE}${path}`;
}

/** Add auth headers and return the fetch credentials mode for the current config. */
function applyAuth(headers: Headers, sendCsrf: boolean): RequestCredentials {
  if (clientConfig.bearerToken) {
    headers.set("Authorization", `Bearer ${clientConfig.bearerToken}`);
    // No cookies: the API prefers a session cookie over the Bearer header.
    return "omit";
  }
  if (sendCsrf) {
    const csrf = readCookie("zs_csrf");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  return "include";
}

let unauthorizedHandler: (() => void) | null = null;

/**
 * Native app: called when the API rejects the Bearer session (401), e.g. after "Salir" on
 * another device or a remote close from Ajustes. Never called in cookie mode (web).
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** Saves a downloaded file; null = browser download (anchor + object URL). */
export type BlobSaver = (blob: Blob, filename: string) => Promise<void>;

let blobSaver: BlobSaver | null = null;

/** Native app: route every download through Filesystem + share sheet. null restores the web path. */
export function setBlobSaver(saver: BlobSaver | null): void {
  blobSaver = saver;
}

function anchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function deliverBlob(blob: Blob, filename: string): Promise<void> {
  if (blobSaver) await blobSaver(blob, filename);
  else anchorDownload(blob, filename);
}

/** Authenticated GET for binary downloads (same auth rules as request()). */
function authFetch(path: string): Promise<Response> {
  const headers = new Headers();
  const credentials = applyAuth(headers, true);
  return fetch(apiUrl(path), { credentials, headers });
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

export class ApiError extends Error {
  constructor(public status: number, public detail: string, public body: any) {
    super(detail || `Error ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers);
  // Do not force JSON Content-Type for FormData (browser sets multipart boundary).
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (init.body && !headers.has("Content-Type") && !isFormData) {
    headers.set("Content-Type", "application/json");
  }

  const credentials = applyAuth(headers, method !== "GET" && method !== "HEAD");
  const res = await fetch(apiUrl(path), { ...init, headers, credentials });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    if (res.status === 401 && clientConfig.bearerToken) unauthorizedHandler?.();
    const detail = (body && (body.detail || body.message)) || res.statusText;
    throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
  }
  if (res.status === 204) return undefined as unknown as T;
  return body as T;
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function post<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: data ? JSON.stringify(data) : undefined });
}
function put<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: "PUT", body: data ? JSON.stringify(data) : undefined });
}
function patch<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: "PATCH", body: data ? JSON.stringify(data) : undefined });
}
function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}
function delJson<T>(path: string, data: unknown): Promise<T> {
  return request<T>(path, { method: "DELETE", body: JSON.stringify(data) });
}
function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1]);
    } catch {
      return utf[1];
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim() ?? fallback;
}

async function downloadGet(path: string, fallbackFilename: string): Promise<void> {
  const res = await authFetch(path);
  if (!res.ok) {
    const text = await res.text();
    const body = text ? safeJson(text) : null;
    const detail = (body && (body.detail || body.message)) || res.statusText;
    throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
  }
  const filename = filenameFromDisposition(res.headers.get("Content-Disposition"), fallbackFilename);
  await deliverBlob(await res.blob(), filename);
}

export const api = {
  health: () => get<{ status: string }>("/health"),

  auth: {
    login: (identifier: string, password: string) =>
      post<SessionInfo>("/api/auth/login", { identifier, password }),
    session: () => get<SessionInfo>("/api/auth/session"),
    /** Closes this device's session only. */
    logout: () => post<void>("/api/auth/logout"),
    /** Closes every session of the account (all devices). */
    logoutAll: () => post<void>("/api/auth/logout-all"),
    getInvite: (token: string) => get<InviteInfo>(`/api/auth/invite/${encodeURIComponent(token)}`),
    acceptInvite: (token: string, password: string) =>
      post<{ ok: boolean; message: string }>(`/api/auth/invite/${encodeURIComponent(token)}`, {
        password,
      }),
    forgotPassword: (identifier: string) =>
      post<ForgotPasswordResult>("/api/auth/forgot-password", { identifier }),
    getReset: (token: string) => get<{ ok: boolean }>(`/api/auth/reset-password/${encodeURIComponent(token)}`),
    resetPassword: (token: string, password: string) =>
      post<ResetPasswordResult>(`/api/auth/reset-password/${encodeURIComponent(token)}`, { password }),
    /** Native app login on the platform host (APP_LOGIN_ENABLED). */
    appLogin: (identifier: string, password: string, deviceName?: string | null) =>
      post<AppLoginResult>("/api/auth/app/login", {
        identifier,
        password,
        device_name: deviceName || null,
      }),
    /** Exchange the single-use ticket from appLogin() for a session in the chosen community. */
    appSession: (ticket: string, slug: string, deviceName?: string | null) =>
      post<AppSession>("/api/auth/app/session", { ticket, slug, device_name: deviceName || null }),
    /** Single-use ticket for the Guard WebSocket (used automatically in Bearer mode). */
    wsTicket: () => post<WsTicket>("/api/auth/ws-ticket"),
  },

  categories: {
    list: () => get<Category[]>("/api/categories"),
    create: (name: string) => post<Category>("/api/categories", { name }),
    remove: (id: string) => del<void>(`/api/categories/${id}`),
  },

  authorizations: {
    list: (active = true) => get<Authorization[]>(`/api/authorizations${active ? "" : "?only_active=false"}`),
    history: () => get<Authorization[]>("/api/authorizations/history"),
    create: (
      data: Partial<Authorization> & {
        visitor_name: string;
        expires_at?: string | null;
        visitor_email?: string | null;
      }
    ) => post<Authorization>("/api/authorizations", data),
    update: (id: string, data: Partial<Authorization>) =>
      patch<Authorization>(`/api/authorizations/${id}`, data),
    revoke: (id: string) => post<Authorization>(`/api/authorizations/${id}/revoke`),
    enroll: (id: string) => get<VisitorTotpEnroll>(`/api/authorizations/${id}/qr`),
    resendInvite: (id: string) => post<Authorization>(`/api/authorizations/${id}/resend-invite`),
    /** @deprecated Use enroll — path kept for compatibility. */
    qr: (id: string) => get<VisitorTotpEnroll>(`/api/authorizations/${id}/qr`),
    batch: (data: {
      title: string;
      guests?: BatchGuestIn[];
      event_date: string;
      notes?: string;
    }) => post<BatchOccasionalResult>("/api/authorizations/batch", { ...data, auth_type: "temporary" }),
  },

  visitorEvents: {
    list: () => get<VisitorEvent[]>("/api/visitor-events"),
    addGuests: (id: string, data: { guests: BatchGuestIn[] }) =>
      post<BatchOccasionalResult>(`/api/visitor-events/${id}/guests`, data),
    updateDate: (id: string, eventDate: string) =>
      patch<VisitorEvent>(`/api/visitor-events/${id}`, { event_date: eventDate }),
    revoke: (id: string) => post<VisitorEvent>(`/api/visitor-events/${id}/revoke`),
  },

  eventJoin: {
    info: (token: string) => get<EventJoinInfo>(`/api/event-join/${encodeURIComponent(token)}`),
    submit: (
      token: string,
      data: { visitor_name: string; phone: string; password: string; document_id?: string | null }
    ) => post<VisitorTotpEnroll>(`/api/event-join/${encodeURIComponent(token)}`, data),
  },

  visitorAccess: {
    info: (token: string) => get<VisitorAccessInfo>(`/api/access/${token}`),
    enroll: (token: string) => get<VisitorTotpEnroll>(`/api/access/${token}/qr`),
  },

  verify: {
    qr: (qrcode: string) => post<VerificationResult>("/api/verify/qr", { token: qrcode }),
  },

  walkIn: {
    create: (data: { resident_user_id: string; visitor_name: string; notes?: string }) =>
      post<WalkInRequest>("/api/walk-in", data),
    pending: () => get<WalkInRequest[]>("/api/walk-in/pending"),
    decide: (id: string, decision: "approved" | "rejected") =>
      post<WalkInRequest>(`/api/walk-in/${id}/decide`, { decision }),
    cancel: (id: string) => post<WalkInRequest>(`/api/walk-in/${id}/cancel`, {}),
  },

  guard: {
    searchResidents: (q: string) =>
      get<GuardResident[]>(`/api/guard/residents?q=${encodeURIComponent(q)}`),
    listEvents: (params?: { q?: string; resident_user_id?: string }) => {
      const q = new URLSearchParams();
      if (params?.q) q.set("q", params.q);
      if (params?.resident_user_id) q.set("resident_user_id", params.resident_user_id);
      const qs = q.toString();
      return get<GuardVisitorEvent[]>(`/api/guard/events${qs ? `?${qs}` : ""}`);
    },
    getEvent: (id: string) => get<GuardVisitorEventDetail>(`/api/guard/events/${id}`),
  },

  accessEvents: {
    register: (data: Partial<AccessEvent> & { visitor_name: string; status: string }) =>
      post<AccessEvent>("/api/access-events", data),
    list: (insideOnly = false) =>
      get<AccessEvent[]>(`/api/access-events${insideOnly ? "?inside_only=true" : ""}`),
  },

  frequent: {
    linkedResidents: () =>
      get<{ resident_user_id: string; full_name: string }[]>("/api/frequent/linked-residents"),
    authorizations: () => get<Authorization[]>("/api/frequent/authorizations"),
    requestRenewal: (data: { resident_user_id: string; visitor_name: string; category_id?: string }) =>
      post<void>("/api/frequent/renewal-request", data),
  },

  admin: {
    users: (q?: string) =>
      get<CommunityMember[]>(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    createUser: (data: CommunityMemberCreate) => post<CommunityMember>("/api/admin/users", data),
    updateUser: (id: string, data: { is_active: boolean }) =>
      patch<CommunityMember>(`/api/admin/users/${id}`, data),
    removeUser: (id: string) => del<void>(`/api/admin/users/${id}`),
    importUsers: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<UserImportResult>("/api/admin/users/import", { method: "POST", body: form });
    },
    downloadImportTemplate: async (format: "csv" | "xlsx" = "csv") => {
      const filename = format === "xlsx" ? "plantilla-usuarios.xlsx" : "plantilla-usuarios.csv";
      await downloadGet(
        `/api/admin/users/import-template?format=${encodeURIComponent(format)}`,
        filename
      );
    },
    resendInvite: (id: string) => post<UserImportResult>(`/api/admin/users/${id}/resend-invite`),
    resendPendingInvites: () => post<UserImportResult>("/api/admin/users/resend-pending-invites"),
    guestAccess: (q?: string) =>
      get<AdminGuestAccessDirectory>(
        `/api/admin/guest-access${q ? `?q=${encodeURIComponent(q)}` : ""}`
      ),
    createAuthorization: (data: {
      visitor_name: string;
      phone?: string | null;
      document_id?: string | null;
      category_id?: string | null;
      auth_type: "one_time" | "temporary";
      expires_at: string;
      notes?: string | null;
    }) => post<Authorization>("/api/admin/authorizations", data),
    revokeAuthorization: (id: string) =>
      post<Authorization>(`/api/admin/authorizations/${id}/revoke`),
    revokeVisitorEvent: (id: string) =>
      post<AdminVisitorEvent>(`/api/admin/visitor-events/${id}/revoke`),
    audit: (params?: { action?: string; date?: string; hour?: number; page?: number; page_size?: number }) => {
      const q = new URLSearchParams();
      if (params?.action) q.set("action", params.action);
      if (params?.date) q.set("date", params.date);
      if (params?.hour != null) q.set("hour", String(params.hour));
      if (params?.page != null) q.set("page", String(params.page));
      if (params?.page_size != null) q.set("page_size", String(params.page_size));
      const qs = q.toString();
      return get<AuditPage>(`/api/admin/audit${qs ? `?${qs}` : ""}`);
    },
    auditActions: () => get<string[]>("/api/admin/audit/actions"),
    downloadAudit: async (params: { from: string; to: string; format?: "csv" | "xlsx" }) => {
      const format = params.format ?? "xlsx";
      const q = new URLSearchParams();
      q.set("from", params.from);
      q.set("to", params.to);
      q.set("format", format);
      const fallback = `auditoria-${params.from.replaceAll("-", "")}-${params.to.replaceAll("-", "")}.${format}`;
      await downloadGet(`/api/admin/audit/export?${q.toString()}`, fallback);
    },
    metrics: () => get<Metrics>("/api/admin/metrics"),
    updateSettings: (settings: Record<string, unknown>) =>
      patch<{ settings: Record<string, unknown> }>("/api/admin/community/settings", settings),
  },

  platform: {
    communities: {
      list: () => get<CommunityAdmin[]>("/api/platform/communities"),
      create: (data: { slug: string; name: string }) =>
        post<CommunityAdmin>("/api/platform/communities", data),
      update: (id: string, data: { name?: string; settings?: Record<string, unknown> }) =>
        patch<CommunityAdmin>(`/api/platform/communities/${id}`, data),
      deactivate: (id: string) => post<CommunityAdmin>(`/api/platform/communities/${id}/deactivate`),
      activate: (id: string) => post<CommunityAdmin>(`/api/platform/communities/${id}/activate`),
      remove: (id: string) => del<void>(`/api/platform/communities/${id}`),
      getSmtp: (id: string) => get<CommunitySmtp>(`/api/platform/communities/${id}/smtp`),
      updateSmtp: (id: string, data: CommunitySmtpInput) =>
        put<CommunitySmtp>(`/api/platform/communities/${id}/smtp`, data),
      testSmtp: (id: string, data?: { to_email?: string }) =>
        post<{ ok: boolean; message: string }>(`/api/platform/communities/${id}/smtp/test`, data),
      listAdmins: (id: string) =>
        get<CommunityAdminMember[]>(`/api/platform/communities/${id}/admins`),
      createAdmin: (id: string, data: { full_name: string; email: string }) =>
        post<CommunityAdminInviteResult>(`/api/platform/communities/${id}/admins`, data),
      resendAdminInvite: (id: string, userId: string) =>
        post<CommunityAdminInviteResult>(
          `/api/platform/communities/${id}/admins/${userId}/resend-invite`
        ),
      removeAdmin: (id: string, userId: string) =>
        del<void>(`/api/platform/communities/${id}/admins/${userId}`),
    },
    smtp: {
      get: () => get<PlatformBillingSettings>("/api/platform/smtp"),
      update: (data: PlatformBillingSettingsInput) =>
        put<PlatformBillingSettings>("/api/platform/smtp", data),
      test: (data?: { to_email?: string }) =>
        post<{ ok: boolean; message: string }>("/api/platform/smtp/test", data),
    },
    contactMessages: {
      list: () => get<ContactMessage[]>("/api/platform/contact-messages"),
      get: (id: string) => get<ContactMessage>(`/api/platform/contact-messages/${id}`),
      markRead: (id: string) => post<ContactMessage>(`/api/platform/contact-messages/${id}/read`),
    },
    billing: {
      accounts: {
        list: () => get<SaasBillingAccount[]>("/api/platform/billing/accounts"),
        get: (id: string) => get<SaasBillingAccount>(`/api/platform/billing/accounts/${id}`),
        create: (data: {
          name: string;
          currency?: BillingCurrency;
          reference_prefix?: string;
          billing_day?: number;
        }) => post<SaasBillingAccount>("/api/platform/billing/accounts", data),
        update: (id: string, data: { name?: string; is_active?: boolean; billing_day?: number }) =>
          patch<SaasBillingAccount>(`/api/platform/billing/accounts/${id}`, data),
        addContact: (id: string, email: string) =>
          post<SaasContactAddResult>(`/api/platform/billing/accounts/${id}/contacts`, { email }),
        resendContactInvite: (id: string, contactId: string) =>
          post<SaasContactAddResult>(
            `/api/platform/billing/accounts/${id}/contacts/${contactId}/resend-invite`
          ),
        removeContact: (id: string, contactId: string) =>
          del<SaasBillingAccount>(`/api/platform/billing/accounts/${id}/contacts/${contactId}`),
        attachCommunity: (id: string, data: { community_id: string; monthly_amount: string }) =>
          post<SaasBillingAccount>(`/api/platform/billing/accounts/${id}/communities`, data),
        detachCommunity: (id: string, communityId: string) =>
          del<SaasBillingAccount>(`/api/platform/billing/accounts/${id}/communities/${communityId}`),
      },
      setCommunityPrice: (communityId: string, monthly_amount: string) =>
        patch<SaasBillingAccount>(`/api/platform/billing/communities/${communityId}/price`, {
          monthly_amount,
        }),
      bankAccounts: {
        list: () => get<SaasPlatformBankAccount[]>("/api/platform/billing/bank-accounts"),
        create: (data: SaasPlatformBankAccountInput) =>
          post<SaasPlatformBankAccount>("/api/platform/billing/bank-accounts", data),
        update: (id: string, data: SaasPlatformBankAccountInput) =>
          patch<SaasPlatformBankAccount>(`/api/platform/billing/bank-accounts/${id}`, data),
      },
      invoices: {
        list: (params?: { account_id?: string; status?: string }) => {
          const q = new URLSearchParams();
          if (params?.account_id) q.set("account_id", params.account_id);
          if (params?.status) q.set("status", params.status);
          const qs = q.toString();
          return get<SaasInvoice[]>(`/api/platform/billing/invoices${qs ? `?${qs}` : ""}`);
        },
        get: (id: string) => get<SaasInvoice>(`/api/platform/billing/invoices/${id}`),
        generate: () =>
          post<{ created: number; overdue: number }>("/api/platform/billing/invoices/generate"),
        updateLine: (
          invoiceId: string,
          lineId: string,
          data: { amount: string; description?: string }
        ) =>
          patch<SaasInvoice>(`/api/platform/billing/invoices/${invoiceId}/lines/${lineId}`, data),
      },
      payments: {
        list: (params?: { queue?: boolean; account_id?: string }) => {
          const q = new URLSearchParams();
          if (params?.queue) q.set("queue", "true");
          if (params?.account_id) q.set("account_id", params.account_id);
          const qs = q.toString();
          return get<SaasPayment[]>(`/api/platform/billing/payments${qs ? `?${qs}` : ""}`);
        },
        get: (id: string) => get<SaasPayment>(`/api/platform/billing/payments/${id}`),
        approve: (id: string, data?: { admin_notes?: string }) =>
          post<SaasPayment>(`/api/platform/billing/payments/${id}/approve`, data ?? {}),
        reject: (id: string, data: { reason: string; admin_notes?: string }) =>
          post<SaasPayment>(`/api/platform/billing/payments/${id}/reject`, data),
        downloadProof: async (paymentId: string, proofId: string, filename: string) => {
          const res = await authFetch(`/api/platform/billing/payments/${paymentId}/proofs/${proofId}/download`);
          if (!res.ok) throw new ApiError(res.status, "No se pudo descargar el comprobante", null);
          await deliverBlob(await res.blob(), filename);
        },
      },
    },
  },

  saasBilling: {
    summary: (accountId?: string) =>
      get<SaasBillingSummary>(
        `/api/saas-billing/summary${accountId ? `?account_id=${encodeURIComponent(accountId)}` : ""}`
      ),
    payments: {
      list: (accountId?: string) =>
        get<SaasPayment[]>(
          `/api/saas-billing/payments${accountId ? `?account_id=${encodeURIComponent(accountId)}` : ""}`
        ),
      create: (data: SaasPaymentCreateInput) => post<SaasPayment>("/api/saas-billing/payments", data),
      uploadProof: (id: string, file: File) => {
        const form = new FormData();
        form.append("file", file);
        return request<SaasPayment>(`/api/saas-billing/payments/${id}/proofs`, {
          method: "POST",
          body: form,
        });
      },
      downloadProof: async (paymentId: string, proofId: string, filename: string) => {
        const res = await authFetch(`/api/saas-billing/payments/${paymentId}/proofs/${proofId}/download`);
        if (!res.ok) throw new ApiError(res.status, "No se pudo descargar el comprobante", null);
        await deliverBlob(await res.blob(), filename);
      },
    },
  },

  assemblies: {
    list: () => get<AssemblyListItem[]>("/api/assemblies"),
    get: (id: string) => get<Assembly>(`/api/assemblies/${id}`),
    create: (data: AssemblyCreateInput) => post<Assembly>("/api/assemblies", data),
    update: (id: string, data: Partial<AssemblyCreateInput>) =>
      patch<Assembly>(`/api/assemblies/${id}`, data),
    remove: (id: string) => del<void>(`/api/assemblies/${id}`),
    publish: (id: string) => post<Assembly>(`/api/assemblies/${id}/publish`),
    close: (id: string) => post<Assembly>(`/api/assemblies/${id}/close`),
    publishResults: (id: string) => post<Assembly>(`/api/assemblies/${id}/publish-results`),
    vote: (id: string, option_id: string) => post<Assembly>(`/api/assemblies/${id}/vote`, { option_id }),
    uploadAttachment: (id: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<Assembly>(`/api/assemblies/${id}/attachments`, { method: "POST", body: form });
    },
    deleteAttachment: (id: string, attachmentId: string) =>
      del<Assembly>(`/api/assemblies/${id}/attachments/${attachmentId}`),
    downloadUrl: (id: string, attachmentId: string) =>
      apiUrl(`/api/assemblies/${id}/attachments/${attachmentId}/download`),
    download: async (id: string, attachmentId: string, filename: string) => {
      const res = await authFetch(`/api/assemblies/${id}/attachments/${attachmentId}/download`);
      if (!res.ok) {
        const text = await res.text();
        const body = text ? safeJson(text) : null;
        const detail = (body && (body.detail || body.message)) || res.statusText;
        throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
      }
      await deliverBlob(await res.blob(), filename);
    },
  },

  communications: {
    list: () => get<CommunicationListItem[]>("/api/communications"),
    get: (id: string) => get<Communication>(`/api/communications/${id}`),
    create: (data: CommunicationCreateInput) => post<Communication>("/api/communications", data),
    update: (id: string, data: Partial<CommunicationCreateInput>) =>
      patch<Communication>(`/api/communications/${id}`, data),
    remove: (id: string) => del<void>(`/api/communications/${id}`),
    publish: (id: string) => post<Communication>(`/api/communications/${id}/publish`),
    markRead: (id: string) => post<Communication>(`/api/communications/${id}/read`),
    delivery: (id: string) => get<CommunicationDeliverySummary>(`/api/communications/${id}/delivery`),
    uploadAttachment: (id: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<Communication>(`/api/communications/${id}/attachments`, {
        method: "POST",
        body: form,
      });
    },
    deleteAttachment: (id: string, attachmentId: string) =>
      del<Communication>(`/api/communications/${id}/attachments/${attachmentId}`),
    download: async (id: string, attachmentId: string, filename: string) => {
      const res = await authFetch(`/api/communications/${id}/attachments/${attachmentId}/download`);
      if (!res.ok) {
        const text = await res.text();
        const body = text ? safeJson(text) : null;
        const detail = (body && (body.detail || body.message)) || res.statusText;
        throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
      }
      await deliverBlob(await res.blob(), filename);
    },
  },

  ledger: {
    list: (params?: {
      year?: number;
      month?: number;
      entry_type?: LedgerListFilter;
      category?: string;
    }) => {
      const q = new URLSearchParams();
      if (params?.year != null) q.set("year", String(params.year));
      if (params?.month != null) q.set("month", String(params.month));
      if (params?.entry_type) q.set("entry_type", params.entry_type);
      if (params?.category) q.set("category", params.category);
      const qs = q.toString();
      return get<LedgerList>(`/api/ledger${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => get<LedgerEntry>(`/api/ledger/${id}`),
    create: (data: LedgerEntryCreateInput) => post<LedgerEntry>("/api/ledger", data),
    update: (id: string, data: LedgerEntryUpdateInput) =>
      patch<LedgerEntry>(`/api/ledger/${id}`, data),
    remove: (id: string) => del<void>(`/api/ledger/${id}`),
    uploadAttachment: (id: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<LedgerEntry>(`/api/ledger/${id}/attachments`, {
        method: "POST",
        body: form,
      });
    },
    deleteAttachment: (id: string, attachmentId: string) =>
      del<LedgerEntry>(`/api/ledger/${id}/attachments/${attachmentId}`),
    download: async (id: string, attachmentId: string, filename: string) => {
      const res = await authFetch(`/api/ledger/${id}/attachments/${attachmentId}/download`);
      if (!res.ok) {
        const text = await res.text();
        const body = text ? safeJson(text) : null;
        const detail = (body && (body.detail || body.message)) || res.statusText;
        throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
      }
      await deliverBlob(await res.blob(), filename);
    },
  },

  commonAreas: {
    list: (includeInactive = false) =>
      get<CommonArea[]>(
        `/api/common-areas${includeInactive ? "?include_inactive=true" : ""}`
      ),
    get: (id: string) => get<CommonArea>(`/api/common-areas/${id}`),
    create: (data: CommonAreaCreateInput) => post<CommonArea>("/api/common-areas", data),
    update: (id: string, data: Partial<CommonAreaCreateInput> & { is_active?: boolean }) =>
      patch<CommonArea>(`/api/common-areas/${id}`, data),
    remove: (id: string) => del<CommonArea>(`/api/common-areas/${id}`),
  },

  reservations: {
    list: (params?: { status?: string; area_id?: string }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      if (params?.area_id) q.set("area_id", params.area_id);
      const qs = q.toString();
      return get<Reservation[]>(`/api/reservations${qs ? `?${qs}` : ""}`);
    },
    calendar: (areaId: string, from: string, to: string) => {
      const q = new URLSearchParams({ area_id: areaId, from, to });
      return get<CalendarEvent[]>(`/api/reservations/calendar?${q}`);
    },
    queue: (areaId?: string) =>
      get<Reservation[]>(
        `/api/reservations/queue${areaId ? `?area_id=${encodeURIComponent(areaId)}` : ""}`
      ),
    create: (data: ReservationCreateInput) => post<Reservation>("/api/reservations", data),
    cancel: (id: string) => post<Reservation>(`/api/reservations/${id}/cancel`),
    approve: (id: string, data?: { amount?: string | number }) =>
      post<Reservation>(`/api/reservations/${id}/approve`, data ?? {}),
    reject: (id: string, rejection_reason: string) =>
      post<Reservation>(`/api/reservations/${id}/reject`, { rejection_reason }),
  },

  contact: (data: { name: string; email: string; community?: string; message: string }) =>
    post<{ ok: boolean; message: string }>("/api/contact", data),

  me: {
    communities: () => get<MembershipCommunity[]>("/api/me/communities"),
    switchCommunity: (community_id: string) =>
      post<SessionInfo>("/api/me/community", { community_id }),
    profile: () => get<ProfileInfo>("/api/me/profile"),
    updateProfile: (data: { full_name?: string; email?: string | null; phone?: string | null }) =>
      patch<ProfileInfo>("/api/me/profile", data),
    changePassword: (current_password: string, new_password: string) =>
      post<void>("/api/me/password", { current_password, new_password }),
    notificationSettings: () => get<NotificationSettings>("/api/me/notification-settings"),
    updateNotificationSettings: (push_enabled: boolean) =>
      patch<NotificationSettings>("/api/me/notification-settings", { push_enabled }),
    visitorAvailability: () => get<VisitorAvailability>("/api/me/visitor-availability"),
    updateVisitorAvailability: (data: {
      status: "receiving" | "confirm" | "paused";
      unavailable_until?: string | null;
    }) => patch<VisitorAvailability>("/api/me/visitor-availability", data),
    vapidPublicKey: () => get<{ public_key: string }>("/api/me/push/vapid-public-key"),
    pushSubscribe: (data: { endpoint: string; p256dh: string; auth: string; user_agent?: string }) =>
      post<void>("/api/me/push/subscribe", data),
    pushUnsubscribe: (endpoint: string) => delJson<void>("/api/me/push/subscribe", { endpoint }),
    /** Devices logged in to this account (current one first). */
    sessions: () => get<SessionDevice[]>("/api/me/sessions"),
    /** Close another device's session remotely. */
    revokeSession: (id: string) => del<void>(`/api/me/sessions/${encodeURIComponent(id)}`),
    /** Native app push (FCM) token for this user + current community. */
    registerDevice: (platform: "android" | "ios", token: string) =>
      post<void>("/api/me/push/device", { platform, token }),
    unregisterDevice: (token: string) => delJson<void>("/api/me/push/device", { token }),
  },

  notifications: {
    list: () => get<InAppNotification[]>("/api/notifications"),
    unreadCount: () => get<{ count: number }>("/api/notifications/unread-count"),
    markRead: (id: string) => post<InAppNotification>(`/api/notifications/${id}/read`),
    markAllRead: () => post<void>("/api/notifications/read-all"),
  },

  billing: {
    statement: (dateFrom?: string, dateTo?: string) => {
      const q = new URLSearchParams();
      if (dateFrom) q.set("date_from", dateFrom);
      if (dateTo) q.set("date_to", dateTo);
      const qs = q.toString();
      return get<BillingStatement>(`/api/billing/statement${qs ? `?${qs}` : ""}`);
    },
    charges: (status = "outstanding") =>
      get<BillingCharge[]>(`/api/billing/charges?status=${encodeURIComponent(status)}`),
    charge: (id: string) => get<BillingCharge>(`/api/billing/charges/${id}`),
    payments: () => get<BillingPayment[]>("/api/billing/payments"),
    createPayment: (data: PaymentCreateInput) => post<BillingPayment>("/api/billing/payments", data),
    submitPayment: (id: string) => post<BillingPayment>(`/api/billing/payments/${id}/submit`),
    uploadProof: (id: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<BillingPayment>(`/api/billing/payments/${id}/proofs`, { method: "POST", body: form });
    },
    downloadProof: async (paymentId: string, proofId: string, filename: string) => {
      const res = await authFetch(`/api/billing/payments/${paymentId}/proofs/${proofId}/download`);
      if (!res.ok) {
        const text = await res.text();
        const body = text ? safeJson(text) : null;
        const detail = (body && (body.detail || body.message)) || res.statusText;
        throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
      }
      await deliverBlob(await res.blob(), filename);
    },
    receipts: () => get<BillingReceipt[]>("/api/billing/receipts"),
    receipt: (id: string) => get<BillingReceipt>(`/api/billing/receipts/${id}`),
    bankAccounts: () => get<BillingBankAccount[]>("/api/billing/bank-accounts"),

    admin: {
      dashboard: () => get<BillingDashboard>("/api/billing/admin/dashboard"),
      units: () => get<BillingUnit[]>("/api/billing/admin/units"),
      charges: (status?: string, unitId?: string) => {
        const q = new URLSearchParams();
        if (status) q.set("status", status);
        if (unitId) q.set("unit_id", unitId);
        const qs = q.toString();
        return get<BillingCharge[]>(`/api/billing/admin/charges${qs ? `?${qs}` : ""}`);
      },
      createCharge: (data: ChargeCreateInput) => post<BillingCharge>("/api/billing/charges", data),
      createChargesBulk: (data: ChargeBulkCreateInput) =>
        post<BillingCharge[]>("/api/billing/charges/bulk", data),
      cancelCharge: (id: string) => post<BillingCharge>(`/api/billing/charges/${id}/cancel`),
      cancelChargesBulk: (data: ChargeCancelBulkInput) =>
        post<ChargeCancelBulkResult>("/api/billing/charges/cancel-bulk", data),
      payments: (status?: string) =>
        get<BillingPayment[]>(
          `/api/billing/admin/payments${status ? `?status=${encodeURIComponent(status)}` : ""}`
        ),
      payment: (id: string) => get<BillingPayment>(`/api/billing/admin/payments/${id}`),
      reviewPayment: (id: string) => post<BillingPayment>(`/api/billing/admin/payments/${id}/review`),
      approvePayment: (id: string, admin_notes?: string) =>
        post<BillingPayment>(`/api/billing/admin/payments/${id}/approve`, { admin_notes }),
      rejectPayment: (id: string, rejection_reason: string) =>
        post<BillingPayment>(`/api/billing/admin/payments/${id}/reject`, { rejection_reason }),
      reversePayment: (id: string, reversal_reason: string) =>
        post<BillingPayment>(`/api/billing/admin/payments/${id}/reverse`, { reversal_reason }),
      downloadProof: async (paymentId: string, proofId: string, filename: string) => {
        const res = await authFetch(`/api/billing/admin/payments/${paymentId}/proofs/${proofId}/download`);
        if (!res.ok) {
          const text = await res.text();
          const body = text ? safeJson(text) : null;
          const detail = (body && (body.detail || body.message)) || res.statusText;
          throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), body);
        }
        await deliverBlob(await res.blob(), filename);
      },
      bankAccounts: () => get<BillingBankAccount[]>("/api/billing/admin/bank-accounts"),
      createBankAccount: (data: BankAccountInput) =>
        post<BillingBankAccount>("/api/billing/admin/bank-accounts", data),
      updateBankAccount: (id: string, data: BankAccountInput) =>
        patch<BillingBankAccount>(`/api/billing/admin/bank-accounts/${id}`, data),
      deleteBankAccount: (id: string) => del<void>(`/api/billing/admin/bank-accounts/${id}`),
      schedules: () => get<BillingSchedule[]>("/api/billing/schedules"),
      createSchedule: (data: ScheduleCreateInput) => post<BillingSchedule>("/api/billing/schedules", data),
      runSchedule: (id: string) => post<BillingCharge[]>(`/api/billing/schedules/${id}/run`),
      aging: (currency: BillingCurrency | string = "NIO") =>
        get<BillingAging>(
          `/api/billing/admin/reports/aging?currency=${encodeURIComponent(currency)}`
        ),
      collections: (dateFrom: string, dateTo: string, currency: BillingCurrency | string = "NIO") =>
        get<BillingCollections>(
          `/api/billing/admin/reports/collections?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}&currency=${encodeURIComponent(currency)}`
        ),
      delinquency: () => get<BillingAging>("/api/billing/admin/reports/delinquency"),
    },
  },
};

function websocketBase(): string {
  if (clientConfig.wsBaseUrl) return clientConfig.wsBaseUrl;
  // A runtime absolute API base (native app) also hosts /ws: http(s) → ws(s).
  if (clientConfig.runtimeBaseUrl && /^https?:\/\//i.test(clientConfig.runtimeBaseUrl)) {
    return clientConfig.runtimeBaseUrl.replace(/^http/i, "ws");
  }
  const configured = (import.meta as any).env?.VITE_WS_URL ?? "";
  return (
    configured ||
    `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`
  );
}

/**
 * Call `open` with the socket URL for the current auth mode. Cookie mode connects
 * directly (synchronously). Bearer mode (native app) first fetches a single-use ticket,
 * because a WebSocket cannot carry the Authorization header; a new ticket is needed for
 * every (re)connect. `onError` runs when the ticket request fails.
 */
function withSocketAuth(
  url: string,
  open: (socketUrl: string) => void,
  onError: () => void,
  isClosed: () => boolean
): void {
  if (!clientConfig.bearerToken) {
    open(url);
    return;
  }
  api.auth
    .wsTicket()
    .then(({ ticket }) => {
      if (!isClosed()) open(`${url}?ticket=${encodeURIComponent(ticket)}`);
    })
    .catch(onError);
}

/** Open a Guard WebSocket for real-time walk-in decisions. Returns a cleanup function. */
export function openGuardSocket(communityId: string, onDecided: (payload: any) => void): () => void {
  const url = `${websocketBase()}/ws/guard/${communityId}`;
  let ws: WebSocket | null = null;
  let closed = false;
  let reconnect: ReturnType<typeof setTimeout>;

  const open = (socketUrl: string) => {
    ws = new WebSocket(socketUrl);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.event === "walk_in:decided") onDecided(msg.data);
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      if (!closed) reconnect = setTimeout(connect, 2000); // backoff reconnect
    };
  };

  const connect = () => {
    if (closed) return;
    withSocketAuth(url, open, () => {
      if (!closed) reconnect = setTimeout(connect, 2000);
    }, () => closed);
  };
  connect();

  return () => {
    closed = true;
    clearTimeout(reconnect);
    ws?.close();
  };
}

/** Open a resident Acceso WebSocket. Refetch lists on `acceso:changed` (ignore ping). */
export function openAccesoSocket(
  communityId: string,
  onChanged: () => void,
  onReconnect?: () => void
): () => void {
  const url = `${websocketBase()}/ws/acceso/${communityId}`;
  let ws: WebSocket | null = null;
  let closed = false;
  let reconnect: ReturnType<typeof setTimeout>;
  let openedOnce = false;

  const open = (socketUrl: string) => {
    ws = new WebSocket(socketUrl);
    ws.onopen = () => {
      if (openedOnce) onReconnect?.();
      openedOnce = true;
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.event === "acceso:changed") onChanged();
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      if (!closed) reconnect = setTimeout(connect, 2000);
    };
  };

  const connect = () => {
    if (closed) return;
    withSocketAuth(url, open, () => {
      if (!closed) reconnect = setTimeout(connect, 2000);
    }, () => closed);
  };
  connect();

  return () => {
    closed = true;
    clearTimeout(reconnect);
    ws?.close();
  };
}
